import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FirebasePerformanceTypes } from '@react-native-firebase/perf';
import { useStoreContext } from 'features/store';

import { IRestaurant } from '@rbi-ctg/store';
import { useMenuExperimentVariantName } from 'experiments/menu-variant/use-menu-experiment-variant-name';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  useOneStoreMenuMetadataQuery,
} from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { DEFAULT_MENU_VARIANT_KEY, MENU_API_URL } from 'remote/constants';
import { LANGUAGES } from 'state/intl/types';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { appVersion } from 'utils/app-version-info';
import { getDateInTimezone } from 'utils/dateTime';
import { getLanguage } from 'utils/intl/language';
import { fetch } from 'utils/network';
import { startTrace } from 'utils/performance/trace';

import { findActiveDayPartIds } from './dayparts';
import {
  IIngredients,
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
  PosServiceMode,
} from './types';
import { IUnifiedMenuContext, UnifiedMenuContext } from './unified-menu-context';
import {
  createIndexedMenu,
  createIndexedOffers,
  createIndexedRewards,
  createMainMenuNode,
} from './utils';

interface ILoadedMenuVariant {
  requestedVariantKey: string;
  resolvedVariantKey: string;
  storeNumber: string | null;
}

// Matches a JSON string literal's contents, handling escaped characters.
const JSON_STRING_VALUE = String.raw`((?:\\.|[^"\\])*)`;

// A plain `{"en":...,"fr":...}` pair (as used by e.g. `ingredients.name`/`components`), in
// either source order. Most localized-text fields (item/offer name, description, image altText)
// are NOT shaped like this — the gateway query (src/queries/gateway/menu.graphql) aliases their
// English field to `locale` at query time, so they arrive as `{"locale":...,"fr":...}` and never
// match these patterns.
const EN_THEN_FR = new RegExp(`"en":"${JSON_STRING_VALUE}","fr":"${JSON_STRING_VALUE}"`, 'g');
const FR_THEN_EN = new RegExp(`"fr":"${JSON_STRING_VALUE}","en":"${JSON_STRING_VALUE}"`, 'g');

/**
 * Rewrites a raw OneMenu JSON payload's localized-text keys to a single `"locale":"..."` key
 * per field, before parsing — avoids updating every `.locale` read in the app to be dynamic per
 * language.
 *
 * For plain `{"en":...,"fr":...}` fields, a blind single-key replace is order-dependent —
 * `JSON.parse` keeps the positionally-last duplicate `locale` key, not whichever replace ran
 * logically last — so it can pick the wrong language if French happens to appear before English
 * in the source. With French active, those pairs are resolved to their winning value explicitly
 * first, independent of source order.
 */
export const applyLocaleTextTransform = (text: string, activeLanguage: LANGUAGES): string => {
  if (activeLanguage !== LANGUAGES.fr) {
    return text.replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
  }

  const withPairsResolved = text
    .replace(EN_THEN_FR, (_match, en, fr) => `"locale":"${fr}"`)
    .replace(FR_THEN_EN, (_match, fr, en) => `"locale":"${fr}"`);

  return withPairsResolved
    .replaceAll(`"${LANGUAGES.fr}":"`, '"locale":"')
    .replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
};

/** Provides the active One Menu, including an assigned Amplitude menu variant when available. */
export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isEnabled = useEnableUnifiedMenu();
  const amplitudeVariantKey = useMenuExperimentVariantName();
  const menuVariantKey = amplitudeVariantKey || DEFAULT_MENU_VARIANT_KEY;
  const { posServiceMode } = useDerivedServiceModeContext();
  const { store, availableDayParts } = useStoreContext();
  const [hasError, setHasError] = useState(false);

  /**
   * Store the last fetch time for each store menu, used to determine if the menu if we need to refetch
   * the full menu or keep the cached version. This being empty means that the menu has never been fetched
   * in the current session.
   */
  const lastFetchTime = useRef<Record<string, number>>({});
  const fetchId = useRef(0);

  const [menu, setMenu] = useState<ILocalizedMenu | undefined>(undefined);
  const [loadedStoreNumber, setLoadedStoreNumber] = useState<string | null>(null);
  const [loadedMenuVariant, setLoadedMenuVariant] = useState<ILoadedMenuVariant>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const metadataMenuVariantKey =
    loadedMenuVariant?.requestedVariantKey === menuVariantKey &&
    loadedMenuVariant.storeNumber === (store?.number ?? null)
      ? loadedMenuVariant.resolvedVariantKey
      : menuVariantKey;

  // Get the last modified time of the menu and refetch if it has changed
  useOneStoreMenuMetadataQuery({
    variables: store?.number
      ? { storeId: store.number, variantName: metadataMenuVariantKey }
      : undefined,
    skip: !isEnabled || !store?.number,
    onCompleted: data => {
      if (data.OneStoreMenuMetadata?.lastModified) {
        const lastModified = Date.parse(data.OneStoreMenuMetadata.lastModified);
        if (lastModified > lastFetchTime.current[store?.number ?? '']) {
          fetchMenu();
        }
      }
    },
  });

  const indexedMenu: IndexedMenu<ILocalizedMenuEntry> = useMemo(
    () => (menu ? createIndexedMenu(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const indexedRewards: IndexedMenu<IRewardEntryDto> = useMemo(
    () => (menu ? createIndexedRewards(menu) : IndexedMenu.EMPTY),
    [menu]
  );
  const indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> = useMemo(
    () => (menu ? createIndexedOffers(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const mainMenu = useMemo(
    () => maybeCreateMainMenu(menu, indexedMenu, posServiceMode, store, availableDayParts),
    [availableDayParts, indexedMenu, menu, posServiceMode, store]
  );

  const fetchMenu = useCallback(async () => {
    let trace: FirebasePerformanceTypes.Trace | null = null;
    const currentFetchId = ++fetchId.current;
    const requestedStoreNumber = store?.number ?? null;
    try {
      setHasError(false);
      setIsLoading(true);

      // new high performance fetch that hits s3 directly which is pre BR compressed
      trace = startTrace(`fetch_1m_s3`, { storeId: store?.number ?? '' });
      const fetchMenuVariant = (variantKey: string): Promise<ILocalizedMenu> =>
        fetch<ILocalizedMenu>(`${MENU_API_URL}/${store?.number}-app-${variantKey}.json`, {
          method: 'GET',
          headers: {
            'x-ui-version': appVersion,
          },
          // unfortunately, this adds ~15ms in debug mode (less in release mode probably),
          // but it's better than updating every `.locale` in the app to be dynamic — see
          // applyLocaleTextTransform for why this isn't a single blind replace.
          textTransform: text => applyLocaleTextTransform(text, getLanguage()),
        });

      const variantKeys = new Set([menuVariantKey, DEFAULT_MENU_VARIANT_KEY]);
      let result: { menu: ILocalizedMenu; variantKey: string } | undefined;
      let lastError: unknown;

      for (const variantKey of variantKeys) {
        try {
          result = {
            menu: await fetchMenuVariant(variantKey),
            variantKey,
          };
          break;
        } catch (error) {
          if (currentFetchId !== fetchId.current) {
            return;
          }
          lastError = error;
        }
      }

      if (!result) {
        throw lastError;
      }

      // ignore stale responses from previous store changes
      if (currentFetchId !== fetchId.current) {
        return;
      }

      setMenu(result.menu);
      setLoadedStoreNumber(requestedStoreNumber);
      setLoadedMenuVariant({
        requestedVariantKey: menuVariantKey,
        resolvedVariantKey: result.variantKey,
        storeNumber: requestedStoreNumber,
      });
      lastFetchTime.current[requestedStoreNumber ?? ''] = Date.now();
    } catch (error) {
      if (currentFetchId !== fetchId.current) {
        return;
      }
      setHasError(true);
      setLoadedStoreNumber(null);
    } finally {
      trace?.stop();
      if (currentFetchId === fetchId.current) {
        setIsLoading(false);
      }
    }
  }, [menuVariantKey, store?.number]);

  useEffect(() => {
    if (isEnabled && store?.number) {
      fetchMenu();
    }
  }, [isEnabled, store?.number, fetchMenu]);

  useEffect(() => {
    setMenu(undefined);
    setLoadedStoreNumber(null);
    setLoadedMenuVariant(undefined);
    setHasError(false);
  }, [menuVariantKey, store?.number]);

  const value: IUnifiedMenuContext = {
    rawMenu: menu,
    mainMenu: mainMenu?.children ?? EMPTY_MENU,
    indexedMenu,
    indexedRewards,
    indexedOffers,
    loadedStoreNumber,
    isLoading,
    refetchMenu: fetchMenu,
    ingredients: (menu?.ingredients as IIngredients[]) ?? [],
    hasError,
  };

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

const EMPTY_MENU: IMainMenuNode[] = [];

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry> | undefined,
  posServiceMode: PosServiceMode | null,
  store: IRestaurant | undefined,
  availableDayParts: IDayPartBoundary[] | undefined
): IMainMenuNode | undefined {
  if (indexedMenu && menu && posServiceMode && store) {
    const now = store.timezone ? getDateInTimezone(store.timezone) : new Date();
    // TODO In future there should be only one source for this: availableDayParts
    const activeDayPartIds = availableDayParts
      ? availableDayParts.map(dp => dp.key)
      : findActiveDayPartIds(menu.dayParts, now);
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode,
      dayPartIds: activeDayPartIds,
    });
  }
  return undefined;
}
